import { useEffect } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
// i18n
import 'src/locales/i18n';
// routes
import Router from 'src/routes/sections';
// theme
import ThemeProvider from 'src/theme';
// hooks
import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
// components
import ProgressBar from 'src/components/progress-bar';
import MotionLazy from 'src/components/animate/motion-lazy';
import { SettingsProvider, SettingsDrawer } from 'src/components/settings';
import ErrorBoundary from 'src/components/ErrorBoundary';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
// auth
import { AuthProvider, AuthConsumer } from 'src/auth/context/jwt';
import { debounce } from 'lodash';
import ReduxProvider from './redux/redux-provider';

// Define the app version
const APP_VERSION = '1.0.4';

// ----------------------------------------------------------------------

export default function App() {
  useScrollToTop();
  //  Check if app version matches the stored version
  useEffect(() => {
    const checkAppVersion = debounce(() => {
      const storedVersion = localStorage.getItem('appVersion');
      if (storedVersion !== APP_VERSION) {
        localStorage.removeItem('settings');
        localStorage.setItem('appVersion', APP_VERSION);
      }
    }, 300);

    checkAppVersion();

    return () => checkAppVersion.cancel();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ReduxProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SettingsProvider
              defaultSettings={{
                themeMode: 'light', // 'light' | 'dark'
                themeDirection: 'ltr', //  'rtl' | 'ltr'
                themeContrast: 'bold', // 'default' | 'bold'
                themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
                themeNavColor: 'dynamic', // 'dynamic' | 'colored'
                themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
                themeStretch: true,
              }}
            >
              <ThemeProvider>
                <MotionLazy>
                  <SettingsDrawer />
                  <ProgressBar />
                  <AuthConsumer>
                    <SnackbarProvider>
                      <Router />
                    </SnackbarProvider>
                  </AuthConsumer>
                </MotionLazy>
              </ThemeProvider>
            </SettingsProvider>
          </LocalizationProvider>
        </ReduxProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
